import BuildConfig from '$extensionSrc/buildConfig'
import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import {
  getOlderThanMonthYear,
  OLDER_THAN_OPTION,
  MONTHS,
} from '$extensionSrc/utils/dateUtils'
import { FBRecommendationKey, PLATFORM } from '$extensionSrc/utils/enums'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { setStoredMachineOverlayStatus } from '../automationStageMachine'
import * as promises from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import { PersistentData, ResumeData } from '../machineInterfaces'
import { getElement } from '../utils'
import {
  getCaseInsensitiveString,
  getCaseInsensitiveXPathTextSelector,
} from '../xpath'
import {
  facebookDetectPasswordPromptWrapper,
  FacebookPasswordPromptException,
  getPasswordPrompt,
} from './facebookPasswordPromptCheck'
import { FacebookRateLimitException } from './facebookRateLimitCheck'
import * as facebookUrls from './fbUrls'

function convertMonthToNumber(month: string): string {
  const months = {
    january: '1',
    february: '2',
    march: '3',
    april: '4',
    may: '5',
    june: '6',
    july: '7',
    august: '8',
    september: '9',
    october: '10',
    november: '11',
    december: '12',
  }
  const lowercaseMonth = month.toLowerCase()
  if (lowercaseMonth in months) {
    return months[lowercaseMonth]
  }

  throw new Error(`Invalid month: ${month}`)
}

export enum FacebookContentType {
  Comment = 'comment',
  Post = 'post',
}

export enum FacebookContentLocation {
  Timeline = 'timeline',
  Group = 'group',
}

export function pluralize(count: number, contentType: FacebookContentType) {
  return `${count} ${contentType}${count === 1 ? '' : 's'}`
}

/*
 * Try to detect if the user is prompted for a password when trying to delete posts
 * But don't wait too long - we do this every time we click the delete button
 */
async function detectPasswordPrompt(): Promise<void> {
  const passwordPrompt = await promises.getWaitForElementToAppearPromise(
    document,
    (node) => getPasswordPrompt(node),
    500,
  )

  if (passwordPrompt) {
    throw new FacebookPasswordPromptException(
      'Facebook password prompt detected while performing bulk action',
    )
  }
}

export async function getLastOptionInFilterMenu(
  modal: HTMLElement,
  dropdownLabel: string,
  listboxLabel: string,
): Promise<HTMLElement> {
  const dropdown = await getElement<HTMLElement>(
    modal,
    `descendant::div[@role="combobox" and ${getCaseInsensitiveXPathTextSelector('aria-label', dropdownLabel)}]`,
  )

  dropdown.click()

  const listbox = await getElement<HTMLElement>(
    document,
    `//div[@role="listbox" and ${getCaseInsensitiveXPathTextSelector('aria-label', listboxLabel)}]`,
  )

  const elements = evaluateXpath<HTMLElement>(
    listbox,
    `descendant::div[@role="option"]`,
  )

  if (!elements || elements.length === 0) {
    throw new Error(`Could not find elements for ${listboxLabel}`)
  }

  const lastElement = elements[elements.length - 1]
  return lastElement
}

export async function openDateFilterModal() {
  const button = await getElement<HTMLElement>(
    document,
    `//div[@role="navigation" and ${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'list of activity log items',
    )}]/descendant::div[
      @role="button" and
      starts-with(${getCaseInsensitiveString('normalize-space(.)')}, "date")
    ]`,
  )

  if (!button) {
    throw new Error(`Could not find button for Date filter`)
  }
  button.click()
  const modal = await getElement<HTMLElement>(
    document,
    `//div[@role="dialog" and ${getCaseInsensitiveXPathTextSelector('aria-label', 'filter')}]`,
  )

  return modal
}

export async function getOldestActivityDate(): Promise<{
  year: string
  month: string
}> {
  // Find date filter selector
  const modal = await openDateFilterModal()

  const year = await getLastOptionInFilterMenu(
    modal,
    'year filter for activity log',
    'select a year to filter activity log entries',
  )
  year.click()

  // Wait for the month filter to appear
  await getElement<HTMLElement>(
    modal,
    `//span[${getCaseInsensitiveString('text()')}="month"]`,
  )

  const month = await getLastOptionInFilterMenu(
    modal,
    'month filter for activity log',
    'select a month to filter activity log',
  )

  return {
    year: year.innerText,
    month: convertMonthToNumber(month.innerText),
  }
}

export function parseYearAndMonthFromUrl(url: string): {
  year: number
  month: number | null
} {
  // parse out query params year and month
  const urlParams = new URL(url).searchParams
  const year = parseInt(urlParams.get('year'), 10)
  const month = urlParams.get('month')
    ? parseInt(urlParams.get('month'), 10)
    : null

  if (!year) {
    throw new Error('Could not find year in URL')
  }

  return {
    year,
    month,
  }
}

export function getSelectAllPostsCheckbox(): HTMLInputElement {
  return evaluateXpath<HTMLInputElement>(
    document,
    `//label[.//span[${getCaseInsensitiveString('text()')}='all']]//input`,
  )[0]
}

export async function getNumberOfPostsSelectedPromise() {
  // Wait for the number of posts selected to appear
  // Don't use getElement here, because we don't want to throw an error if it doesn't appear
  return promises.getWaitForElementToAppearPromise(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//div[${getCaseInsensitiveXPathTextSelector('aria-label', 'activity log item')}]//span[normalize-space() and not(translate(normalize-space(), '0123456789', ''))]`,
      )[0],
    500,
  )
}

export async function loadAllPostsAvailable() {
  let numTryNoChange = 0
  let prevScrollHeight = window.scrollY

  while (numTryNoChange < 5) {
    window.scrollBy(0, window.innerHeight)
    await promises.wait(500)

    // Check if no change based on scroll position
    if (window.scrollY === prevScrollHeight) {
      numTryNoChange += 1
    } else {
      numTryNoChange = 0
    }

    prevScrollHeight = window.scrollY
  }
}

async function selectPostsAndGetDeselectButton(
  selectAllCheckbox: HTMLInputElement,
): Promise<HTMLElement> {
  // Select all posts
  selectAllCheckbox.click()

  let deselectButton = await promises.getWaitForElementToAppearPromise(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//div[@role='button' and ${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'deselect',
        )}]`,
      )[0],
  )

  if (!deselectButton) {
    // try again
    selectAllCheckbox.click()
    deselectButton = await promises.getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          `//div[@role='button' and ${getCaseInsensitiveXPathTextSelector(
            'aria-label',
            'deselect',
          )}]`,
        )[0],
      1500,
    )
    if (!deselectButton) {
      throw new Error('Could not find deselect button')
    }
  }
  return deselectButton
}

export async function getNumberOfPostsAvailable(
  selectAllCheckbox: HTMLInputElement,
): Promise<number> {
  const deselectButton =
    await selectPostsAndGetDeselectButton(selectAllCheckbox)

  let numPostsSelectedElement = await getNumberOfPostsSelectedPromise()

  if (numPostsSelectedElement) {
    // If the number of posts is already available, return it
    deselectButton.click()
    return parseInt(numPostsSelectedElement?.innerText, 10)
  }

  // Sometimes, the number of posts is not available immediately - we need to uncheck one of the posts
  // to force the number of posts to be available
  const nextCheckbox = evaluateXpath<HTMLInputElement>(
    selectAllCheckbox,
    "following::input[@type='checkbox' and @aria-checked][1]",
  )[0]
  nextCheckbox.click()

  numPostsSelectedElement = await getNumberOfPostsSelectedPromise()

  if (!numPostsSelectedElement) {
    // Try again
    nextCheckbox.click() // Deselect again
    numPostsSelectedElement = await getNumberOfPostsSelectedPromise()
    if (!numPostsSelectedElement) {
      // If we can't get a number of posts to appear, this means there is one post available, and
      // the "number of selected posts" is always "All" or empty
      deselectButton.click()
      return 1
    }
  }

  const numPostsSelected = parseInt(numPostsSelectedElement?.innerText, 10)

  deselectButton.click() // deselect all posts to reset
  return numPostsSelected + 1
}

/*
 * This function cehcks for a warning at the top of the page that says Facebook won't let us
 * delete more posts. If it is still there, we refresh the page to make the stage action start over.
 *
 * We store the number of refreshes in persistentData so that we can keep track of how many times
 * we've refreshed the page. If we refresh 20 times, we throw an error and stop the automation.
 *
 * We reset numRefreshes every time the warning disappears.
 */
async function waitForDeletionAllowed(persistentData: PersistentData) {
  const checkForElement = promises.getWaitForElementToDisappearPromise(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//h2//span[${getCaseInsensitiveString('text()')}='we are still processing your changes']`,
      )[0],
    500,
  )

  try {
    // If the promise resolves, it means the processing message is gone
    remoteLog.debug(
      `Facebook is done processing changes. Proceeding with deletion...`,
    )
    await checkForElement
    // Processing is complete, reset numRefreshes and return
    await persistentData.set({
      ...persistentData.data,
      numRefreshes: 0,
    })
  } catch (_error) {
    const numRefreshes = persistentData?.data?.numRefreshes || 0
    if (numRefreshes >= 20) {
      throw new FacebookRateLimitException(
        'Facebook is taking too long to process changes. Please try again later.',
      )
    }
    remoteLog.debug(
      `Facebook is still processing changes. Refreshing... (${numRefreshes}/20 so far)`,
    )
    await persistentData.set({
      ...persistentData.data,
      numRefreshes: numRefreshes + 1,
    })
    await promises.wait(500)
    window.location.reload()
  }
}

export async function removeAllAvailablePosts(
  selectAllCheckbox: HTMLInputElement,
  deleteButtonLabels: string[],
  modalTitle: string,
  confirmDeleteButtonLabel: string,
  isDry: boolean,
): Promise<void> {
  await selectPostsAndGetDeselectButton(selectAllCheckbox)

  const removeButton = deleteButtonLabels
    .map((label) => {
      const button = evaluateXpath<HTMLElement>(
        selectAllCheckbox,
        `following::div[@role='button' and ${getCaseInsensitiveXPathTextSelector('aria-label', label)}]`,
      )[0]
      return button
    })
    .find((button) => button !== undefined)

  if (!removeButton) {
    throw new Error(
      `Could not find remove button for any of these labels: ${deleteButtonLabels.join(', ')}`,
    )
  }

  removeButton.click()

  const modal = await getElement<HTMLElement>(
    document,
    `//div[@role='dialog' and ${getCaseInsensitiveXPathTextSelector('aria-label', modalTitle)}]`,
  )

  const confirmDeleteButton = await getElement<HTMLElement>(
    modal,
    `descendant::div[@role='button' and ${getCaseInsensitiveXPathTextSelector('aria-label', confirmDeleteButtonLabel)}]`,
  )

  if (!isDry) {
    confirmDeleteButton.click()
  }

  // wait for loading spinner to appear
  const spinner = await promises.getWaitForElementToAppearPromise(
    document,
    (node) => evaluateXpath<HTMLElement>(node, `//circle`)[0],
    2000,
  )

  if (spinner) {
    await promises.getWaitForElementToDisappearPromise(
      document,
      (node) => evaluateXpath<HTMLElement>(node, `//circle`)[0],
      5000,
    )
  }
  // Manual check for password prompt - we have a wrapper checking for this
  // in the stage action, but we want to check for it here too
  // because this is where it is most likely to appear,
  // its possible the page refreshes before the wrapper sends the interrupt
  await detectPasswordPrompt()
}

export function incrementYearAndMonth(
  year: number,
  month: number,
): { year: number; month: number } {
  if (month >= 12) {
    return {
      year: year + 1,
      month: 1,
    }
  }

  return {
    year,
    month: month + 1,
  }
}

async function setUpdateStageStatus(
  postType: string,
  dateOption: string,
  olderThanMonth: number,
  olderThanYear: number,
  nDeleted: number,
): Promise<void> {
  const allPostsMsg = `${BuildConfig.TENANT_NAME} is deleting your ${postType}...`
  const olderThanPostsMsg =
    `${BuildConfig.TENANT_NAME} is deleting ${postType} you made before ` +
    `${MONTHS[olderThanMonth]} 1, ${olderThanYear}`

  await setStoredMachineOverlayStatus(PLATFORM.FACEBOOK, {
    body: 'This may take a few minutes',
    title:
      dateOption !== OLDER_THAN_OPTION.NOW ? olderThanPostsMsg : allPostsMsg,
    nDeleted,
  })
}

type FbPostDeleteState = {
  count: number // total count of posts deleted
  message: string
  result?: {
    title: string
    detail?: string
    helper?: {
      text: string
      link: string
      linkText: string
    }
  }
  numPosts?: number // num posts that user has. gathered from profile
  mostRecentPostDate?: string
  username?: string
  year?: string
  month?: string
}

// Constructs the result object that should be saved to initialState storage
function buildResultObject(
  numPostsDeletedAllTime: number,
  numPostsDeletedThisTime: number,
  dateOption: OLDER_THAN_OPTION,
  selectedMonth: number,
  selectedYear: number,
  oldestYear: string,
  oldestMonth: string,
  contentType: string,
  userId: string,
): FbPostDeleteState {
  const deletedDetail =
    `Your ${contentType}` +
    `${dateOption !== OLDER_THAN_OPTION.NOW ? ` made before ${MONTHS[selectedMonth]} ${selectedYear}` : ''}` +
    ' have been deleted.'

  return {
    year: oldestYear,
    month: oldestMonth,
    count: numPostsDeletedAllTime,
    message: `Deleted ${numPostsDeletedAllTime} total ${contentType}.`,
    result: {
      title:
        numPostsDeletedThisTime > 0
          ? `You deleted ${numPostsDeletedThisTime} ${contentType}`
          : `No ${contentType} deleted`,
      detail:
        numPostsDeletedThisTime > 0
          ? deletedDetail
          : `${BuildConfig.TENANT_NAME} did not find any ${contentType} to delete.`,
      helper: contentType === 'posts' && {
        text: `Remember, deleted content lives in the trash for 30 days 
                before being permanently deleted. If you'd like it permanently deleted, 
                you can do so by clearing your trash. `,
        link: facebookUrls.getTrashUrl(userId),
        linkText: 'See your trash here',
      },
    },
  }
}

export function getFacebookLoadOldestPostStageAction(): (
  _machineContext: any,
  userContext: any,
) => Promise<void> {
  const stageAction = async (_machineContext, userContext) => {
    const { userId } = userContext

    const activityLogUrl = facebookUrls.getActivityLogUrl(
      userId,
      facebookUrls.FacebookContentCategoryKey.PERSONAL_INFO,
    )

    window.location.assign(activityLogUrl)
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}

export function getFacebookSaveOldestPostStageAction(
  initialValuesSettingKey: FacebookSettingKey,
  recommendationKey: FBRecommendationKey,
): (_machineContext: any, userContext: any) => Promise<void> {
  const stageAction = async (_machineContext, userContext) => {
    const { updateStoredInitialValues } = userContext

    const { year, month } = await getOldestActivityDate()

    await updateStoredInitialValues(
      {
        [initialValuesSettingKey]: {
          year,
          month,
        },
      },
      recommendationKey,
    )
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}

export function getFacebookDeleteCleanupAction(
  categoryKey: facebookUrls.FacebookContentCategoryKey,
  pageTitle: string,
) {
  const stageAction = async (_machineContext: any, userContext: any) => {
    const { userId } = userContext
    const activityLogUrl = facebookUrls.getActivityLogUrl(userId, categoryKey)
    window.location.assign(activityLogUrl)

    await promises.getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          `//h2[${getCaseInsensitiveString('normalize-space(.)')}="${pageTitle}"]`,
        )[0],
    )
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}

export function getFacebookDeleteBeginUpdateStageAction(
  recommendationKey: FBRecommendationKey,
): (_machineContext: any, userContext: any) => Promise<void> {
  const stageAction = async (_machineContext, userContext) => {
    getFacebookLoadOldestPostStageAction()(_machineContext, userContext)
    const { updateStatisticsLastCount } = userContext
    await updateStatisticsLastCount(recommendationKey, 0)
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}

export function getFacebookLoadDeleteContentCategoryStageAction(
  initialValuesSettingKey: FacebookSettingKey,
  categoryKey: facebookUrls.FacebookContentCategoryKey,
): (_machineContext: any, userContext: any) => Promise<void> {
  const stageAction = async (_machineContext, userContext) => {
    const { userId, initialValues } = userContext

    const initialDate = initialValues?.[initialValuesSettingKey]
    const year = initialDate?.year ?? '2004' // Facebook started in 2004
    const activityLogUrl = facebookUrls.getActivityLogUrl(
      userId,
      categoryKey,
      year,
    )
    window.location.assign(activityLogUrl)
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}

async function removePostsAndUpdateUserContext({
  selectAllCheckbox,
  deletePostLabels,
  deletePostModalTitle,
  confirmDeleteLabel,
  isDry,
  numPostsAvailable,
  recommendationKey,
  resumeData,
  lastUpdatedCount,
  totalPostsDeleted,
  selectedDateOption,
  olderThanMonth,
  olderThanYear,
  contentType,
  userId,
  incrementTotalCount,
  updateStatisticsLastCount,
  updateStoredInitialValues,
  initialValues,
  initialValuesSettingKey,
}: {
  selectAllCheckbox: HTMLInputElement
  deletePostLabels: string[]
  deletePostModalTitle: string
  confirmDeleteLabel: string
  isDry: boolean
  numPostsAvailable: number
  recommendationKey: FBRecommendationKey
  resumeData: ResumeData
  lastUpdatedCount: number
  totalPostsDeleted: number
  selectedDateOption: OLDER_THAN_OPTION
  olderThanMonth: number
  olderThanYear: number
  contentType: string
  userId: string
  incrementTotalCount: (
    recommendationKey: string,
    numPosts: number,
  ) => Promise<void>
  updateStatisticsLastCount: (
    recommendationKey: string,
    numPosts: number,
  ) => Promise<void>
  updateStoredInitialValues: (
    initialValues: Record<string, any>,
    recommendationKey: string,
  ) => Promise<void>
  initialValues: Record<string, any>
  initialValuesSettingKey: FacebookSettingKey
}) {
  await removeAllAvailablePosts(
    selectAllCheckbox,
    deletePostLabels,
    deletePostModalTitle,
    confirmDeleteLabel,
    isDry,
  )

  await resumeData.set({
    ...resumeData.data,
    deletedCount: lastUpdatedCount + numPostsAvailable,
  })

  await incrementTotalCount(recommendationKey, numPostsAvailable)

  await updateStatisticsLastCount(
    recommendationKey,
    lastUpdatedCount + numPostsAvailable,
  )

  await updateStoredInitialValues(
    {
      [initialValuesSettingKey]: buildResultObject(
        totalPostsDeleted + numPostsAvailable,
        lastUpdatedCount + numPostsAvailable,
        selectedDateOption,
        olderThanMonth,
        olderThanYear,
        initialValues?.[initialValuesSettingKey]?.year,
        initialValues?.[initialValuesSettingKey]?.month,
        contentType,
        userId,
      ),
    },
    recommendationKey,
  )
}

/*
 * Delete posts from one year or month of the activity log. The algorithm is as follows:
 * 1. Begin by looking at the earliest year in the activity log
 * 2. If the year has less than page of posts (<25), delete all posts on that page and move on to the next year
 * 3. If the year has more than a page of posts, switch to month view.
 * 4. In month view, scroll to the bottom of the page to load all posts, then delete
 * 5. Then move on to the next month and until the end of the year, and switch back to year view
 */
export function getFacebookDeleteContentCategoryForMonthStageAction(
  categoryKey: facebookUrls.FacebookContentCategoryKey,
  setting: FacebookSettingKey,
  recommendationKey: FBRecommendationKey,
  postType: string,
  categoryPageTitle: string,
  deletePostLabels: string[],
  deletePostModalTitle: string,
  confirmDeleteLabel: string,
  contentType: string,
  initialValuesSettingKey: FacebookSettingKey,
): (
  _machineContext: any,
  userContext: any,
  additionalMachineData: any,
) => Promise<void> {
  const stageAction = async (
    _machineContext,
    userContext,
    additionalMachineData,
  ) => {
    // wait for page title to appear, but continue if we don't find it
    // no need to error if page title changes, intent is to make sure the page is loaded before we start
    const pageTitle = await promises.getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          `//h2[${getCaseInsensitiveString('normalize-space(.)')}="${categoryPageTitle}"]`,
        )[0],
    )
    if (!pageTitle) {
      remoteLog.debug(`Load page for ${FacebookSettingKey}: 
        did not find page title ${categoryPageTitle}. Continuing.`)
    }

    const {
      incrementTotalCount,
      settingsSelected,
      userId,
      recommendationProgress,
      isDry,
      updateStoredInitialValues,
      initialValues,
      updateStatisticsLastCount,
    } = userContext

    if (!settingsSelected[setting]?.isSelected) {
      return
    }

    const { resumeData, persistentData } = additionalMachineData

    // make sure deletion is allowed before proceeding with the stage:
    await waitForDeletionAllowed(persistentData)

    let lastUpdatedCount =
      recommendationProgress?.[recommendationKey].statistics
        ?.lastUpdatedCount || 0

    let totalPostsDeleted =
      recommendationProgress?.[recommendationKey].statistics?.totalCount || 0

    const { year, month } = parseYearAndMonthFromUrl(window.location.href)

    const selectedDateOption =
      settingsSelected?.[initialValuesSettingKey]?.dateOption ||
      OLDER_THAN_OPTION.NOW
    const [olderThanMonth, olderThanYear] = getOlderThanMonthYear(
      selectedDateOption,
      settingsSelected?.[initialValuesSettingKey]?.sinceDateMonth ||
        new Date().getMonth().toString(),
      settingsSelected?.[initialValuesSettingKey]?.sinceDateYear ||
        new Date().getFullYear().toString(),
    )
    const fbOlderThanMonth = olderThanMonth + 1 // Convert to 1-indexed month for facebook

    await setUpdateStageStatus(
      postType,
      selectedDateOption,
      olderThanMonth,
      olderThanYear,
      lastUpdatedCount,
    )

    let nextUrl
    if (year === olderThanYear && !month) {
      // switch to month-by-month for the last year of our delete window
      nextUrl = facebookUrls.getActivityLogUrl(userId, categoryKey, year, 1)
    } else if (year === olderThanYear && month >= fbOlderThanMonth) {
      // do nothing - this stage should complete and move on to the next stage
      remoteLog.debug(
        `Reached the end of the delete window for ${categoryKey} at ${year}-${month}`,
      )
    } else {
      const selectAllCheckbox = getSelectAllPostsCheckbox()

      if (month) {
        // If we are looking at just a month, we can
        // scroll to the bottom of the page to load all posts
        if (selectAllCheckbox && !selectAllCheckbox.disabled) {
          await loadAllPostsAvailable()
        }
      }

      let numPostsAvailable = 0
      if (selectAllCheckbox && !selectAllCheckbox.disabled) {
        numPostsAvailable = await getNumberOfPostsAvailable(selectAllCheckbox)
      }

      const onlyOnePage = numPostsAvailable < 25

      if ((month || onlyOnePage) && numPostsAvailable > 0) {
        await removePostsAndUpdateUserContext({
          selectAllCheckbox,
          deletePostLabels,
          deletePostModalTitle,
          confirmDeleteLabel,
          isDry,
          numPostsAvailable,
          recommendationKey,
          resumeData,
          lastUpdatedCount,
          totalPostsDeleted,
          selectedDateOption,
          olderThanMonth,
          olderThanYear,
          contentType,
          userId,
          incrementTotalCount,
          updateStatisticsLastCount,
          updateStoredInitialValues,
          initialValues,
          initialValuesSettingKey,
        })

        lastUpdatedCount += numPostsAvailable
        totalPostsDeleted += numPostsAvailable
      }
      // Proceed to the next month, or switch back to year view for a new year
      if (month) {
        const { year: newYear, month: newMonth } = incrementYearAndMonth(
          year,
          month,
        )
        if (newYear !== year && newYear < olderThanYear) {
          // switch back to year view
          nextUrl = facebookUrls.getActivityLogUrl(userId, categoryKey, newYear)
        } else {
          // continue going month by month for the rest of the year
          nextUrl = facebookUrls.getActivityLogUrl(
            userId,
            categoryKey,
            newYear,
            newMonth,
          )
        }
      } else if (!onlyOnePage) {
        // Switch to month view
        nextUrl = facebookUrls.getActivityLogUrl(userId, categoryKey, year, 1)
      } else if (year + 1 === olderThanYear) {
        // Next year is the last year in our delete window, so go month by month
        nextUrl = facebookUrls.getActivityLogUrl(
          userId,
          categoryKey,
          year + 1,
          1,
        )
      } else {
        // Move on to the next year
        nextUrl = facebookUrls.getActivityLogUrl(userId, categoryKey, year + 1)
      }

      await setUpdateStageStatus(
        postType,
        selectedDateOption,
        olderThanMonth,
        olderThanYear,
        lastUpdatedCount,
      )
    }

    if (nextUrl) {
      // navigate to the url for the next page of posts
      const urlChangePromise = promises.getUrlChangePromise()
      window.location.assign(nextUrl)
      await urlChangePromise
    }
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}

export function getFacebookDeleteContentFinishStageAction(
  recommendationKey: FBRecommendationKey,
  initialValuesSettingKey: FacebookSettingKey,
  contentType: string,
  contentTypeKey: facebookUrls.FacebookContentCategoryKey,
  pageTitle: string,
): (_machineContext: any, userContext: any) => Promise<void> {
  const stageAction = async (_machineContext, userContext) => {
    // This stage adds a result object to the first setting key,
    // initialValuesSettingKey - this is because tasks expect
    // the result object to be in the first setting key

    // First, navigate the user to a useful page to end the automation
    await getFacebookDeleteCleanupAction(contentTypeKey, pageTitle)(
      _machineContext,
      userContext,
    )

    const {
      settingsSelected,
      updateStoredInitialValues,
      recommendationProgress,
      initialValues,
      userId,
    } = userContext

    const totalPostsDeleted =
      recommendationProgress?.[recommendationKey].statistics?.totalCount || 0
    const totalPostsDeletedThisTime =
      recommendationProgress?.[recommendationKey].statistics
        ?.lastUpdatedCount || 0

    const year = initialValues?.[initialValuesSettingKey]?.year || '2004'
    const month = initialValues?.[initialValuesSettingKey]?.month || '1'

    const selectedDateOption =
      settingsSelected?.[initialValuesSettingKey]?.dateOption ||
      OLDER_THAN_OPTION.NOW
    const [olderThanMonth, olderThanYear] = getOlderThanMonthYear(
      selectedDateOption,
      settingsSelected?.[initialValuesSettingKey]?.sinceDateMonth ||
        new Date().getMonth().toString(),
      settingsSelected?.[initialValuesSettingKey]?.sinceDateYear ||
        new Date().getFullYear().toString(),
    )

    await updateStoredInitialValues(
      {
        [initialValuesSettingKey]: buildResultObject(
          totalPostsDeleted,
          totalPostsDeletedThisTime,
          selectedDateOption,
          olderThanMonth,
          olderThanYear,
          year,
          month,
          contentType,
          userId,
        ),
      },
      recommendationKey,
    )
  }

  return facebookDetectPasswordPromptWrapper(stageAction)
}
